import { z } from "zod";

/**
 * Contrato do Dashboard do projeto. Sem Kommo mapeado a API devolve zeros;
 * com conexao, os numeros reais no mesmo formato.
 *
 * Dinheiro em centavos inteiros (nunca float) e so numeros BASE: lucro, ticket
 * medio, ROAS e taxas de conversao sao calculados a partir deles num lugar so.
 */

/** Etapas do funil, na ordem. Do lead no gerenciador de anuncios a venda. */
export const ETAPAS_DO_FUNIL = ["gerenciador", "crm", "mql", "agendado", "consulta", "venda"] as const;
export const etapaDoFunilSchema = z.enum(ETAPAS_DO_FUNIL);
export type EtapaDoFunil = z.infer<typeof etapaDoFunilSchema>;

/** Periodo inclusivo, em datas de calendario (sem hora nem fuso). */
export const periodoSchema = z
  .object({ inicio: z.iso.date(), fim: z.iso.date() })
  .refine((periodo) => periodo.inicio <= periodo.fim, {
    message: "o inicio precisa ser antes do fim",
  });
export type Periodo = z.infer<typeof periodoSchema>;

const centavos = z.number().int().min(0);
const quantidade = z.number().int().min(0);

export const metricasDoPeriodoSchema = z.object({
  faturamentoCentavos: centavos,
  /** Soma do campo Valor Consulta de quem passou na etapa de Consulta. */
  faturamentoConsultaCentavos: centavos,
  /** Soma do valor de venda de quem passou na etapa de Tratamento. */
  faturamentoTratamentoCentavos: centavos,
  /** `null` enquanto nenhuma fonte de midia (Meta/Google) estiver conectada. */
  investimentoCentavos: centavos.nullable(),
  /** Gasto da plataforma. `null` quando ela nao esta conectada; `0` quando nao gastou. */
  metaCentavos: centavos.nullable().optional(),
  googleCentavos: centavos.nullable().optional(),
  /** Vendas fechadas no periodo (base do ticket medio). */
  vendas: quantidade,
  /**
   * Propostas em aberto de quem passou pela consulta (posicao ATUAL). `null` no
   * periodo anterior, porque o CRM nao guarda essa posicao no passado.
   */
  valorEmAbertoCentavos: centavos.nullable(),
});
export type MetricasDoPeriodo = z.infer<typeof metricasDoPeriodoSchema>;

export const funilSchema = z.object({
  /** Leads reportados pelo gerenciador de anuncios; `null` sem Meta/Google conectado. */
  gerenciador: quantidade.nullable(),
  crm: quantidade,
  mql: quantidade,
  agendado: quantidade,
  consulta: quantidade,
  venda: quantidade,
});
export type Funil = z.infer<typeof funilSchema>;

/**
 * Indicadores operacionais (RF-11, RF-12, RF-15 e RF-16). Cada um pode faltar
 * (`null`) quando a fonte nao fornece o dado: ausencia nunca vira zero.
 */
export const operacaoSchema = z.object({
  /** Mediana de dias entre a entrada no CRM e a venda, nas vendas do periodo. */
  cicloDeVendasDias: z.number().min(0).nullable(),
  /** Leads ativos no pipeline AGORA (posicao atual, nao do fim do periodo). */
  leadsAtivosNoPipeline: quantidade.nullable(),
  /** Mediana, em minutos, da entrada do lead a primeira resposta humana. */
  tempoDeRespostaMinutos: z
    .object({
      horarioComercial: z.number().min(0).nullable(),
      foraDoHorario: z.number().min(0).nullable(),
    })
    .nullable(),
  /** Fracao de leads com origem rastreada (0.96 = 96%). */
  precisaoDoRastreio: z.number().min(0).max(1).nullable(),
  /**
   * Leads do periodo cujo contato no Kommo e de um dia anterior ao da
   * oportunidade (paciente antigo).
   */
  pacientesAntigos: quantidade,
});
export type Operacao = z.infer<typeof operacaoSchema>;

/** Uma oportunidade da copia local do CRM. Contato nao entra (LGPD). */
export const oportunidadeSchema = z.object({
  leadId: z.number().int(),
  nome: z.string(),
  origem: z.string().nullable(),
  utmSource: z.string().nullable(),
  utmCampaign: z.string().nullable(),
  utmConjunto: z.string().nullable(),
  utmAnuncio: z.string().nullable(),
  criadoEm: z.iso.datetime(),
  /** Quando o lead chegou a etapa filtrada. */
  chegouEm: z.iso.datetime().nullable(),
  etapaAtual: z.enum(["crm", "mql", "agendado", "consulta", "venda"]).nullable(),
  valorCentavos: z.number().int().min(0),
  /** Minutos ate a primeira resposta humana. `null` quando ninguem respondeu. */
  primeiraRespostaMinutos: z.number().min(0).nullable(),
});
export type Oportunidade = z.infer<typeof oportunidadeSchema>;

export type NoDoRankingUtm = {
  readonly nivel: "campanha" | "conjunto" | "anuncio";
  readonly nome: string | null;
  readonly oportunidades: number;
  readonly vendas: number;
  readonly taxa: number;
  readonly filhos: readonly NoDoRankingUtm[];
};

export const noDoRankingUtmSchema: z.ZodType<NoDoRankingUtm> = z.lazy(() =>
  z.object({
    nivel: z.enum(["campanha", "conjunto", "anuncio"]),
    nome: z.string().nullable(),
    oportunidades: quantidade,
    vendas: quantidade,
    taxa: z.number().min(0).max(1),
    filhos: z.array(noDoRankingUtmSchema),
  }),
);
export type RankingUtm = NoDoRankingUtm;

export const paginaDeOportunidadesSchema = z.object({
  dados: z.array(oportunidadeSchema),
  proximoCursor: z.string().nullable(),
  total: z.number().int().min(0),
  /** Ranking do recorte inteiro (nao so da pagina). */
  rankingUtm: z.array(noDoRankingUtmSchema),
});
export type PaginaDeOportunidades = z.infer<typeof paginaDeOportunidadesSchema>;

const NIVEIS_DA_UTM = [
  { nivel: "campanha", chave: "utmCampaign" },
  { nivel: "conjunto", chave: "utmConjunto" },
  { nivel: "anuncio", chave: "utmAnuncio" },
] as const;

/** Campanha → conjunto → anúncio. Quem vendeu mais primeiro em cada nível. */
export function rankingPorUtm(
  linhas: readonly Pick<Oportunidade, "utmCampaign" | "utmConjunto" | "utmAnuncio" | "etapaAtual">[],
): NoDoRankingUtm[] {
  return agruparRanking(linhas, NIVEIS_DA_UTM);
}

function agruparRanking(
  linhas: readonly Pick<Oportunidade, "utmCampaign" | "utmConjunto" | "utmAnuncio" | "etapaAtual">[],
  niveis: readonly (typeof NIVEIS_DA_UTM)[number][],
): NoDoRankingUtm[] {
  const [atual, ...resto] = niveis;
  if (!atual || linhas.length === 0) return [];
  if (!linhas.some((linha) => linha[atual.chave])) return agruparRanking(linhas, resto);

  const grupos = new Map<string, typeof linhas>();
  for (const linha of linhas) {
    const chave = linha[atual.chave] ?? "";
    grupos.set(chave, [...(grupos.get(chave) ?? []), linha]);
  }
  return [...grupos.entries()]
    .map(([chave, grupo]) => {
      const vendas = grupo.filter((linha) => linha.etapaAtual === "venda").length;
      const oportunidades = grupo.length;
      return {
        nivel: atual.nivel,
        nome: chave === "" ? null : chave,
        oportunidades,
        vendas,
        taxa: oportunidades === 0 ? 0 : vendas / oportunidades,
        filhos: agruparRanking(grupo, resto),
      };
    })
    .sort(
      (a, b) =>
        b.vendas - a.vendas ||
        b.taxa - a.taxa ||
        b.oportunidades - a.oportunidades ||
        (a.nome ?? "").localeCompare(b.nome ?? "", "pt-BR"),
    )
    .filter((no) => no.nome !== null || no.filhos.length > 0);
}

/** Contato com as oportunidades dele, da mais antiga para a mais nova. */
export const contatoDoHistoricoSchema = z.object({
  contatoId: z.number().int(),
  nome: z.string(),
  criadoEm: z.iso.datetime().nullable(),
  oportunidades: z.array(oportunidadeSchema),
});
export type ContatoDoHistorico = z.infer<typeof contatoDoHistoricoSchema>;

export const paginaDeHistoricoDeContatosSchema = z.object({
  dados: z.array(contatoDoHistoricoSchema),
  proximoCursor: z.string().nullable(),
  total: z.number().int().min(0),
});
export type PaginaDeHistoricoDeContatos = z.infer<typeof paginaDeHistoricoDeContatosSchema>;

export const resumoDoDashboardSchema = z.object({
  periodo: periodoSchema,
  /** Mesmo tamanho do periodo, logo antes dele: base das variacoes. */
  periodoAnterior: periodoSchema,
  atual: metricasDoPeriodoSchema,
  anterior: metricasDoPeriodoSchema,
  funil: funilSchema,
  operacao: operacaoSchema,
  /** Quando os dados do CRM foram sincronizados pela ultima vez. */
  atualizadoEm: z.iso.datetime().nullable(),
});
export type ResumoDoDashboard = z.infer<typeof resumoDoDashboardSchema>;
